import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getStaticQuestions,
  bulkDeleteStaticQuestions,
  PaginatedStaticQuestionResponse,
} from '../../Game/api/gameApi';
import QuestionEditModal from './QuestionEditModal';
import Pagination from './Pagination';

const PAGE_SIZE = 20;

const QuestionManagementTab: React.FC = () => {
  const [data, setData] = useState<PaginatedStaticQuestionResponse | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<number | 'new' | null>(null);
  const [ordering, setOrdering] = useState('id');

  const toggleOrdering = (field: 'id' | 'guardian' | 'sdg_numbers') => {
    setOrdering((prev) => (prev === field ? `-${field}` : field));
    setPage(1);
  };

  const requestIdRef = useRef(0);

  const loadData = useCallback(async (targetPage: number, targetOrdering: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getStaticQuestions({ page: targetPage, pageSize: PAGE_SIZE, ordering: targetOrdering });
      if (requestId !== requestIdRef.current) return; // a newer request superseded this one
      setData(res);
      setSelectedIds(new Set());
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load questions');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  useEffect(() => {
    loadData(page, ordering);
  }, [page, ordering, loadData]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data) return;
    setSelectedIds((prev) =>
      prev.size === data.results.length ? new Set() : new Set(data.results.map((r) => r.id)),
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} question(s)? This action cannot be undone.`)) {
      return;
    }
    try {
      setIsDeleting(true);
      const deletedCount = selectedIds.size;
      await bulkDeleteStaticQuestions(Array.from(selectedIds));

      const remaining = Math.max(0, (data?.count ?? 0) - deletedCount);
      const newTotalPages = Math.max(1, Math.ceil(remaining / PAGE_SIZE));
      if (page > newTotalPages) {
        setPage(newTotalPages); // the effect reloads the new page
      } else {
        await loadData(page, ordering);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete questions.');
    } finally {
      setIsDeleting(false);
    }
  };

  const truncate = (text: string, max = 100) =>
    text.length > max ? text.substring(0, max) + '...' : text;

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Question Pool</h2>
          <p className="text-sm text-gray-600">{data?.count ?? 0} total questions</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedIds.size > 0 && (
            <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
          )}
          <button
            onClick={handleDeleteSelected}
            disabled={selectedIds.size === 0 || isDeleting}
            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            {isDeleting ? 'Deleting...' : `Delete Selected${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
          </button>
          <button
            onClick={() => loadData(page, ordering)}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => setEditingQuestionId('new')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            + Add Question
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={!!data && data.results.length > 0 && selectedIds.size === data.results.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-blue-600 rounded"
                />
              </th>
              <th
                onClick={() => toggleOrdering('id')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
              >
                ID {ordering === 'id' ? '▲' : ordering === '-id' ? '▼' : ''}
              </th>
              <th
                onClick={() => toggleOrdering('guardian')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
              >
                Guardian {ordering === 'guardian' ? '▲' : ordering === '-guardian' ? '▼' : ''}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
              <th
                onClick={() => toggleOrdering('sdg_numbers')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
              >
                SDGs {ordering === 'sdg_numbers' ? '▲' : ordering === '-sdg_numbers' ? '▼' : ''}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">Loading questions...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-red-600">{error}</td>
              </tr>
            ) : !data || data.results.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">No questions found</td>
              </tr>
            ) : (
              data.results.map((q) => (
                <tr key={q.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(q.id)}
                      onChange={() => toggleSelect(q.id)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{q.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{q.guardian || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{truncate(q.question_text)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {q.sdg_numbers && q.sdg_numbers.length > 0 ? q.sdg_numbers.join(', ') : 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                  <button
                    onClick={() => setEditingQuestionId(q.id)}
                    className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors"
                  >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
            {/* Pagination */}
            {data && data.count > 0 && (
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-gray-600">
              Showing {((page - 1) * PAGE_SIZE) + 1} to {Math.min(page * PAGE_SIZE, data.count)} of {data.count} results
            </div>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              disabled={loading}
            />
          </div>
        </div>
      )}

      {editingQuestionId !== null && (
        <QuestionEditModal
          questionId={editingQuestionId === 'new' ? null : editingQuestionId}
          onClose={() => setEditingQuestionId(null)}
          onSaved={() => loadData(page, ordering)}
        />
      )}
    </div>
  );
};

export default QuestionManagementTab;